import { useCallback } from 'react'
import { useAuth } from 'react-oidc-context'
import { useGTMTracking } from './'
import {
  PolicyProfessionInfoEventData,
  PolicyholderDetailsEventData,
  ErrorCapturedEventData,
  QuotationInfoEventData,
  QuoteViewedEventData,
  BenefitLinkClickedEventData,
  PayClickedEventData,
  PaymentStartedEventData,
  PaymentSuccessEventData,
  PaymentFailedEventData,
  NavigationMenuEventData,
  PurchasedPolicyOptionEventData,
  MMPPolicyActionEventData,
  PolicyRenewalStartedEventData,
  PolicyRenewedEventData,
  PolicyRenewalFailedEventData,
} from '../types'
import { QuotationModel } from '@/features/quote-list/types'

export const useTrackingEvents = () => {
  const gtmTracking = useGTMTracking()
  const { user } = useAuth()

  const trackPolicyholderDetails = useCallback(
    (data: PolicyholderDetailsEventData) => {
      const dateOfBirth: string | undefined = user?.profile?.DateOfBirth
      const [dobYear, dobMonth] = dateOfBirth?.split('-') ?? []
      gtmTracking.trackPurchaseStart({
        dobMonth,
        dobYear,
        userId: user?.profile?.sub,
      })
      gtmTracking.trackPolicyholderDetails({
        identityNumber: data.identityNumber,
        monthOfBirth: String(data.monthOfBirth),
        yearOfBirth: data.yearOfBirth,
      })
    },
    [gtmTracking, user]
  )

  const trackAddProfessionData = useCallback(
    (professionInfo: PolicyProfessionInfoEventData) => {
      gtmTracking.trackAddProfessionData(professionInfo)
    },
    [gtmTracking]
  )

  const trackAddDeclarationsDetailsData = useCallback(
    (declarationsDetails: Record<string, boolean>) => {
      gtmTracking.trackAddDeclarationsDetailsData(declarationsDetails)
    },
    [gtmTracking]
  )

  const trackQuotationsReceived = useCallback(
    (quotationsReceived: QuotationModel[], specialty?: string) => {
      gtmTracking.trackQuotationsReceived(quotationsReceived, specialty)
    },
    [gtmTracking]
  )

  const trackQuoteViewed = useCallback(
    (quoteData: QuoteViewedEventData) => {
      gtmTracking.trackQuoteViewed(quoteData)
    },
    [gtmTracking]
  )

  const trackBenefitLinkClicked = useCallback(
    (data: BenefitLinkClickedEventData) => {
      gtmTracking.trackBenefitLinkClicked(data)
    },
    [gtmTracking]
  )

  const trackQuoteSelected = useCallback(
    (quoteSelected: QuotationInfoEventData) => {
      gtmTracking.trackSelectItem(quoteSelected)
    },
    [gtmTracking]
  )

  const trackPayClicked = useCallback(
    (data: PayClickedEventData) => {
      gtmTracking.trackPayClicked(data)
    },
    [gtmTracking]
  )

  const trackPaymentStarted = useCallback(
    (paymentStarted: PaymentStartedEventData) => {
      gtmTracking.trackPolicyPaymentStarted(paymentStarted)
    },
    [gtmTracking]
  )

  const trackPaymentSuccess = useCallback(
    (paymentSuccess: PaymentSuccessEventData) => {
      gtmTracking.trackPurchaseComplete(paymentSuccess)
    },
    [gtmTracking]
  )

  const trackPaymentFailed = useCallback(
    (data: PaymentFailedEventData) => {
      gtmTracking.trackPaymentFailed(data)
    },
    [gtmTracking]
  )

  const trackNavigationMenu = useCallback(
    (data: NavigationMenuEventData) => {
      gtmTracking.trackNavigationMenu(data)
    },
    [gtmTracking]
  )

  const trackPurchasedPolicyOption = useCallback(
    (data: PurchasedPolicyOptionEventData) => {
      gtmTracking.trackPurchasedPolicyOption(data)
    },
    [gtmTracking]
  )

  const trackMMPPolicyAction = useCallback(
    (data: MMPPolicyActionEventData) => {
      gtmTracking.trackMMPPolicyAction(data)
    },
    [gtmTracking]
  )

  const trackPolicyRenewalStarted = useCallback(
    (data: PolicyRenewalStartedEventData) => {
      gtmTracking.trackPolicyRenewalStarted(data)
    },
    [gtmTracking]
  )

  const trackPolicyRenewed = useCallback(
    (data: PolicyRenewedEventData) => {
      gtmTracking.trackPolicyRenewed(data)
    },
    [gtmTracking]
  )

  const trackPolicyRenewalFailed = useCallback(
    (data: PolicyRenewalFailedEventData) => {
      gtmTracking.trackPolicyRenewalFailed(data)
    },
    [gtmTracking]
  )

  const trackErrorCaptured = useCallback(
    (error: ErrorCapturedEventData) => {
      gtmTracking.trackErrorCaptured(error)
    },
    [gtmTracking]
  )

  const trackLogin = useCallback(
    (userId?: string) => {
      gtmTracking.trackLogin({ userId: userId ?? user?.profile?.sub })
    },
    [gtmTracking, user]
  )

  const trackLogout = useCallback(() => {
    gtmTracking.trackLogout()
  }, [gtmTracking])

  return {
    trackPolicyholderDetails,
    trackAddProfessionData,
    trackAddDeclarationsDetailsData,
    trackQuotationsReceived,
    trackQuoteViewed,
    trackBenefitLinkClicked,
    trackQuoteSelected,
    trackPayClicked,
    trackPaymentStarted,
    trackPaymentSuccess,
    trackPaymentFailed,
    trackNavigationMenu,
    trackPurchasedPolicyOption,
    trackMMPPolicyAction,
    trackPolicyRenewalStarted,
    trackPolicyRenewed,
    trackPolicyRenewalFailed,
    trackErrorCaptured,
    trackLogin,
    trackLogout,
  }
}
